/**
 * Modules a firm can switch off in Company Settings (OL-135). Keys mirror
 * config/modules.php; the chosen ones reach the SPA as `hidden_modules` on the
 * user. `titles` are mega-menu sections to drop, `pathPrefixes` catch the
 * module's links wherever they sit (for example under Basic Setup).
 */
export const MODULES = [
  {
    key: 'billing',
    label: 'Billing',
    description: 'Invoices, flat fees, expenses, items and the billing tabs on matters and contacts.',
    titles: ['billing'],
    pathPrefixes: ['/billing/'],
  },
  {
    key: 'referrals',
    label: 'Referrals',
    description: 'Referral attorney registrations and referred contacts.',
    titles: ['referrals'],
    pathPrefixes: ['/account/home/referral', '/account/home/referred-contact-list'],
  },
  {
    key: 'secure_file_sharing',
    label: 'Secure File Sharing',
    description: 'Sharing files with clients through PIN-protected links.',
    pathPrefixes: ['/shared-storage'],
  },
  {
    key: 'reports',
    label: 'Reports',
    description: 'AR, raw data, ad hoc and case-count reports.',
    titles: ['reports'],
    pathPrefixes: ['/reports/'],
  },
  {
    key: 'data_import',
    label: 'Data Import',
    description: 'Spreadsheet imports of contacts, matters and invoices.',
    pathPrefixes: ['/data-import'],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    description: 'Mass mailings, email templates and intake forms.',
    titles: ['marketing'],
    pathPrefixes: ['/mass-mailing/', '/intake-forms'],
  },
  {
    key: 'documents',
    label: 'Documents',
    description: 'E-signature documents, document and letter templates, demand letters.',
    titles: ['documents'],
    pathPrefixes: ['/documents/', '/letter-templates', '/demand-letters'],
  },
];

const lower = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

/** The module keys a user's firm has hidden, or [] when the payload has none. */
export const hiddenModulesOf = (user) =>
  Array.isArray(user?.hidden_modules) ? user.hidden_modules : [];

/** Drop the hidden modules from a menu tree; a group left with no links goes too. */
export const applyHiddenModules = (items, hiddenKeys = []) => {
  const hidden = MODULES.filter((module) => hiddenKeys.includes(module.key));
  if (hidden.length === 0) return items;

  const titles = new Set(hidden.flatMap((module) => module.titles || []).map(lower));
  const prefixes = hidden.flatMap((module) => module.pathPrefixes || []);
  const pathHidden = (path) => typeof path === 'string' && prefixes.some((prefix) => path.startsWith(prefix));

  const prune = (children) =>
    children
      .filter((child) => !pathHidden(child.path))
      .map((child) => (child.children?.length ? { ...child, children: prune(child.children) } : child))
      .filter((child) => child.path || !child.children || child.children.length > 0);

  return items
    .filter((item) => !titles.has(lower(item.title)) && !pathHidden(item.path))
    .map((item) => (item.children?.length ? { ...item, children: prune(item.children) } : item))
    .filter((item) => item.path || !item.children || item.children.length > 0);
};
